import { computed, ref } from 'vue';
import { useQuery, useInfiniteQuery } from '@tanstack/vue-query';
import type { InfiniteData } from '@tanstack/vue-query';
import { useProductPage } from './use-product-page';
import { productPageFeedback } from '@/shared/apollo-api/api/product-page-feedback';
import { FeedbackFilterType, FeedbackSortType } from '@/shared/apollo-api/__generated__/types';
import type { Feedback } from '@/shared/apollo-api/__generated__/types';

interface FeedbackPage {
  feedbacks: Feedback[];
  hasMore: boolean;
  currentPage?: number;
}

export function useFeedback() {
  const { state } = useProductPage();

  // Computed properties
  const productId = computed(() => state.product?.id || null);
  const feedbackQuantity = computed(() => state.product?.feedbackQuantity || 0);
  const externalRating = computed(() => state.product?.externalRating || false);
  const rating = computed(() => state.product?.rating || 0);

  // Constants
  const PAGE_SIZE = 10;

  // Query keys
  const feedbackQueryKey = (filters: FeedbackFilterType[], sort: FeedbackSortType) => [
    'feedback',
    productId.value,
    filters,
    sort,
  ];
  const feedbackWithPhotosQueryKey = () => ['feedback-with-photos', productId.value];

  // Feedback with photos query
  const {
    data: feedbackWithPhotosData,
    isLoading: feedbackWithPhotosLoading,
    isFetching: feedbackWithPhotosFetching,
    hasNextPage: hasMoreFeedbackWithPhotos,
    fetchNextPage: loadMoreFeedbackWithPhotos,
    refetch: refetchFeedbackWithPhotos,
  } = useInfiniteQuery<FeedbackPage, Error, FeedbackPage, (string | number)[], number>({
    queryKey: feedbackWithPhotosQueryKey(),
    queryFn: async ({ pageParam = 0 }) => {
      if (!productId.value)
        return { feedbacks: [], hasMore: false, currentPage: pageParam as number };

      const result = await productPageFeedback({
        productId: productId.value,
        page: pageParam as number,
        size: PAGE_SIZE,
        filters: [FeedbackFilterType.Photos],
        sort: FeedbackSortType.Relevance,
      });

      const feedbacks = result.data?.productPage?.feedbacks || [];
      const currentPage = pageParam as number;

      // Проверяем, есть ли еще отзывы с фото: получили полную страницу
      // Для отзывов с фото мы не знаем точное количество, поэтому проверяем только размер страницы
      const hasMore = feedbacks.length === PAGE_SIZE;

      return {
        feedbacks,
        hasMore,
        currentPage,
      };
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.hasMore) return undefined;
      // Возвращаем номер следующей страницы
      return (lastPage.currentPage ?? 0) + 1;
    },
    initialPageParam: 0,
    enabled: computed(() => !!productId.value),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const feedbackWithPhotos = computed(() => {
    if (!feedbackWithPhotosData.value) return [];
    return (
      (feedbackWithPhotosData.value as any).pages?.flatMap(
        (page: FeedbackPage) => page.feedbacks
      ) || []
    );
  });

  // Filtered feedback query factory
  const createFilteredFeedbackQuery = (filters: FeedbackFilterType[], sort: FeedbackSortType) => {
    return useQuery({
      queryKey: feedbackQueryKey(filters, sort),
      queryFn: async () => {
        if (!productId.value) return [];

        const result = await productPageFeedback({
          productId: productId.value,
          page: 0,
          size: PAGE_SIZE,
          filters,
          sort,
        });

        return result.data?.productPage?.feedbacks || [];
      },
      enabled: computed(() => !!productId.value),
      staleTime: 1000 * 60 * 5, // 5 minutes
    });
  };

  // State for current filters and sort
  const currentFilters = ref<FeedbackFilterType[]>([]);
  const currentSort = ref<FeedbackSortType>(FeedbackSortType.Relevance);

  // Expose current sort for UI as computed
  const currentSortValue = computed(() => currentSort.value);

  // Filtered feedback query
  const {
    data: filteredFeedbackData,
    isLoading: filteredFeedbackLoading,
    isFetching: filteredFeedbackFetching,
    hasNextPage: hasMoreFilteredFeedback,
    fetchNextPage: loadMoreFilteredFeedback,
    refetch: refetchFilteredFeedback,
  } = useInfiniteQuery<
    FeedbackPage,
    Error,
    FeedbackPage,
    (string | number | FeedbackFilterType[])[],
    number
  >({
    queryKey: computed(() => feedbackQueryKey(currentFilters.value, currentSort.value)),
    queryFn: async ({ pageParam = 0 }) => {
      if (!productId.value)
        return { feedbacks: [], hasMore: false, currentPage: pageParam as number };

      const result = await productPageFeedback({
        productId: productId.value,
        page: pageParam as number,
        size: PAGE_SIZE,
        filters: currentFilters.value,
        sort: currentSort.value,
      });

      const feedbacks = result.data?.productPage?.feedbacks || [];
      const currentPage = pageParam as number;

      // Проверяем, есть ли еще отзывы: получили полную страницу И есть еще непрочитанные
      const totalLoaded = (currentPage + 1) * PAGE_SIZE;
      const hasMore = feedbacks.length === PAGE_SIZE && totalLoaded < feedbackQuantity.value;

      return {
        feedbacks,
        hasMore,
        currentPage,
      };
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.hasMore) return undefined;
      // Возвращаем номер следующей страницы
      return (lastPage.currentPage ?? 0) + 1;
    },
    initialPageParam: 0,
    enabled: computed(() => !!productId.value),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Computed filtered feedback array
  const filteredFeedback = computed(() => {
    if (!filteredFeedbackData.value) return [];
    return (
      (filteredFeedbackData.value as any).pages?.flatMap((page: FeedbackPage) => page.feedbacks) ||
      []
    );
  });

  // Methods for external use
  function loadFeedback(
    page: number = 0,
    size: number = PAGE_SIZE,
    filters: FeedbackFilterType[] = [],
    sort: FeedbackSortType = FeedbackSortType.Relevance
  ) {
    currentFilters.value = filters;
    currentSort.value = sort;

    if (page === 0) {
      refetchFilteredFeedback();
    } else {
      loadMoreFilteredFeedback();
    }
  }

  function resetFeedback() {
    // Reset the filtered feedback query
    refetchFilteredFeedback();
  }

  function resetFeedbackWithPhotos() {
    // Reset the infinite query
    refetchFeedbackWithPhotos();
  }

  async function initializeFeedback() {
    // Both queries will be enabled automatically when productId is available
    // No need to manually trigger them
  }

  return {
    // State
    feedback: filteredFeedback,
    feedbackWithPhotos,
    feedbackLoading: filteredFeedbackLoading,
    feedbackWithPhotosLoading,
    hasMoreFeedback: hasMoreFilteredFeedback,
    hasMoreFeedbackWithPhotos,

    // Computed
    productId,
    feedbackQuantity,
    externalRating,
    rating,

    // Methods
    loadFeedback,
    loadMoreFeedback: loadMoreFilteredFeedback,
    loadMoreFeedbackWithPhotos,
    resetFeedback,
    resetFeedbackWithPhotos,
    initializeFeedback,
    createFilteredFeedbackQuery,
    currentSortValue,
  };
}
